import './OptionsMenu.css';
import DotsIcon from '../../svgs/dotsIcon';
import { TrashIcon } from '../../svgs';
import { CSSProperties, ReactChild, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CheckIcon from '@material-ui/icons/Check';
/**
 *
 * @param options: { label: String, action: () => void, icon: String | component }[]
 * @param selectedItem?: String
 */

type OptionsItem = {
  label: string,
  action: (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void,
  icon?: ReactChild,
  key?: string | number,
  deleteAction?: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void,
}

type Props = {
  selectedItem?: string,
  options: OptionsItem[],
  children?: ReactNode,
  style?: CSSProperties,
  isMenuLeft?: boolean
  disabled?: boolean
  overrideDisplayWithNoOptions?: boolean
}

export default function OptionsMenu({ selectedItem, options, children, style, isMenuLeft, disabled = false, overrideDisplayWithNoOptions = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top?: number, bottom?: number, left?: number, right?: number }>({ top: 0 });

  const updatePosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const margin = 5;

    // Horizontal alignment mirrors the original CSS: right edge of the menu
    // aligned to the trigger (default), or left edge for `isMenuLeft`.
    const horizontal = isMenuLeft
      ? { left: rect.left }
      : { right: window.innerWidth - rect.right };

    // Flip the menu above the trigger when there isn't enough room below it and
    // there is more room above (e.g. for rows near the bottom of the viewport).
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < menuHeight + margin && spaceAbove > spaceBelow;

    if (openUpward) {
      setCoords({ bottom: window.innerHeight - rect.top + margin, ...horizontal });
    } else {
      setCoords({ top: rect.bottom + margin, ...horizontal });
    }
  }, [isMenuLeft]);

  // Position the menu before paint so it never flashes in the wrong spot.
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // Close on outside click, Escape, or when the page scrolls/resizes (the menu
  // is fixed-positioned in a portal, so it would otherwise detach from the row).
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReposition = () => setOpen(false);

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open]);

  const toggleOpen = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(prev => !prev);
  }, []);

  if (!overrideDisplayWithNoOptions && (!options || options.length == 0)) return null;

  return (
    <div className="sp-action-icon" ref={containerRef} style={style}>
      <button className={`${children ? 'trigger' : 'defaultTrigger'}`} onClick={toggleOpen} disabled={disabled}>
        {children || <DotsIcon size="16px" color="var(--gray-700)" />}
      </button>

      {open && options?.length > 0 && createPortal(
        <div
          ref={menuRef}
          className={`sp-action-menu ${isMenuLeft ? 'menuLeft' : ''}`}
          style={{
            display: 'block',
            position: 'fixed',
            // Force the unused axis to 'auto' so the stylesheet's `top` rule
            // can't combine with our `bottom` and stretch the menu's height.
            top: coords.top ?? 'auto',
            bottom: coords.bottom ?? 'auto',
            left: coords.left ?? 'auto',
            right: coords.right ?? 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map(option => (
            <a key={option.key || option.label} href="#" onClick={e => {
              e.preventDefault();
              option.action();
              setOpen(false);
            }} className={`sp-action-menu-item ${option.label == selectedItem ? 'optionsMenuSelected' : '' }`}>
              <span className="sp-action-menu-item-content">
                {typeof option.icon === 'string' ? <i className={`fa ${option.icon}`}></i> : option.icon}
                {option.label}
              </span>
              {option.deleteAction && (
                <div
                  className="sp-action-menu-item-delete"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    option.deleteAction?.(e);
                  }}
                >
                  <TrashIcon size="14px" color="var(--gray-700)" />
                </div>
              )}
              {option.label == selectedItem && <CheckIcon style={{ width: '16px' }} />}
          </a>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
